import {
  Box,
  Drawer,
  DrawerContent,
  DrawerOverlay,
  Flex,
  IconButton,
  useDisclosure,
  VStack,
  HStack,
  Text,
  Tag,
  Button,
  Link as ChakraLink,
  Image,
  Collapse,
  Icon,
  useColorMode,
  useToast,
} from "@chakra-ui/react";
import { HamburgerIcon, MoonIcon, SunIcon } from "@chakra-ui/icons";
import { IconChevronDown, IconChevronRight } from "@tabler/icons-react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { useLogout } from "@refinedev/core";
import React from "react";
import { AdminNotificationBell } from "./AdminNotificationBell";
import { setupAdminWebPush } from "../../services/adminWebPush";
import {
  ADMIN_ENVIRONMENTS,
  getAdminEnvironment,
  isAdminEnvironmentAvailable,
  setAdminEnvironment,
  type AdminEnvironment,
} from "../../services/adminEnvironment";

interface MenuChildItem {
  name: string;
  label: string;
  route: string;
  group?: string;
}

interface MenuItem {
  name: string;
  label: string;
  icon: React.ReactElement;
  route: string;
  children?: MenuChildItem[];
}

interface CustomLayoutProps {
  children: React.ReactNode;
  Title: React.FC;
  menuItems: MenuItem[];
}

const groupChildren = (children: MenuChildItem[]) => {
  const groups: { group: string; items: MenuChildItem[] }[] = [];
  children.forEach((child) => {
    const groupName = child.group || '';
    const existing = groups.find((g) => g.group === groupName);
    if (existing) {
      existing.items.push(child);
    } else {
      groups.push({ group: groupName, items: [child] });
    }
  });
  return groups;
};

const isRouteActive = (pathname: string, route: string) =>
  pathname === route || pathname.startsWith(route + '/');

const SidebarContent = ({
  menuItems,
  onNavigate,
  ...props
}: {
  menuItems: CustomLayoutProps["menuItems"];
  onNavigate?: () => void;
  [key: string]: any;
}) => {
  const location = useLocation();
  const [openMenus, setOpenMenus] = React.useState<Record<string, boolean>>({});

  React.useEffect(() => {
    menuItems.forEach((item) => {
      if (item.children?.some((child) => isRouteActive(location.pathname, child.route))) {
        setOpenMenus((prev) => ({ ...prev, [item.name]: true }));
      }
    });
  }, []);

  const toggleMenu = (name: string) => {
    setOpenMenus((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  return (
    <Box
      as="nav"
      aria-label="주요 메뉴"
      className="app-sidebar"
      pos="fixed"
      top="0"
      left="0"
      zIndex="sticky"
      h="full"
      w="240px"
      bg="var(--app-sidebar-bg)"
      color="white"
      overflowX="hidden"
      overflowY="auto"
      {...props}
    >
      <Flex className="app-sidebar__brand" px="5" py="5" align="center">
        <ChakraLink
          as={RouterLink}
          to="/dashboard"
          aria-label="운영 개요로 이동"
          title="운영 개요"
          display="block"
          mx="auto"
          borderRadius="md"
          _hover={{ opacity: 0.86 }}
          _focusVisible={{ outline: "2px solid", outlineColor: "var(--app-accent)", outlineOffset: "4px" }}
        >
          <Image src='/assets/quazar_icon.png' alt="Quazar" width="118px" />
        </ChakraLink>
      </Flex>
      <Box px="5" pb="4">
        <Text className="app-sidebar__eyebrow">OPERATIONS CONSOLE</Text>
        <Text className="app-sidebar__context">감정 운영 센터</Text>
      </Box>
      <VStack as="nav" spacing="1" p="2" align="stretch">
        {menuItems.map((item) => {
          if (item.children && item.children.length > 0) {
            const isMenuOpen = openMenus[item.name] ?? false;
            const isParentActive = item.children.some(child => isRouteActive(location.pathname, child.route));
            const hasGroups = item.children.some((c) => c.group);
            const groups = hasGroups ? groupChildren(item.children) : null;

            return (
              <Box key={item.name}>
                {/* 부모 메뉴 - 클릭하면 드롭다운 토글 */}
                <Flex
                  alignItems="center"
                  justifyContent="space-between"
                  p="3"
                  borderRadius="lg"
                  className="app-nav-parent"
                  cursor="pointer"
                  bg={isParentActive ? "var(--app-sidebar-active)" : "transparent"}
                  _hover={{ bg: "var(--app-sidebar-hover)" }}
                  onClick={() => toggleMenu(item.name)}
                >
                  <Flex alignItems="center">
                    {item.icon}
                    <Text ml="3">{item.label}</Text>
                  </Flex>
                  <Icon
                    as={isMenuOpen ? IconChevronDown : IconChevronRight}
                    w={4} h={4}
                    color="whiteAlpha.600"
                  />
                </Flex>

                {/* 하위 메뉴 - 드롭다운 */}
                <Collapse in={isMenuOpen} animateOpacity>
                  <VStack spacing="0" pl="6" align="stretch">
                    {groups ? groups.map((g) => (
                      <Box key={g.group} mt="2">
                        <Text
                          fontSize="sm"
                          fontWeight="bold"
                          color="var(--app-sidebar-muted)"
                          px="3"
                          pt="2"
                          pb="1"
                          borderLeft="2px solid"
                          borderColor="var(--app-accent)"
                          ml="2"
                        >
                          {g.group}
                        </Text>
                        {g.items.map((child) => (
                          <ChakraLink
                            key={child.name}
                            as={RouterLink}
                            to={child.route}
                            aria-current={isRouteActive(location.pathname, child.route) ? "page" : undefined}
                            display="flex"
                            alignItems="center"
                            p="2"
                            pl="5"
                            borderRadius="lg"
                            className="app-nav-link"
                            fontSize="md"
                            color="var(--app-sidebar-text)"
                            bg={isRouteActive(location.pathname, child.route) ? "var(--app-accent)" : "transparent"}
                            _hover={{ bg: "var(--app-sidebar-hover)", textDecoration: "none", color: "white" }}
                            onClick={onNavigate}
                          >
                            <Text>{child.label}</Text>
                          </ChakraLink>
                        ))}
                      </Box>
                    )) : item.children.map((child) => (
                      <ChakraLink
                        key={child.name}
                        as={RouterLink}
                        to={child.route}
                        aria-current={isRouteActive(location.pathname, child.route) ? "page" : undefined}
                        display="flex"
                        alignItems="center"
                        p="2"
                        pl="3"
                        borderRadius="lg"
                        className="app-nav-link"
                        fontSize="md"
                        bg={isRouteActive(location.pathname, child.route) ? "var(--app-accent)" : "transparent"}
                        _hover={{ bg: "var(--app-sidebar-hover)", textDecoration: "none" }}
                        onClick={onNavigate}
                      >
                        <Text>{child.label}</Text>
                      </ChakraLink>
                    ))}
                  </VStack>
                </Collapse>
              </Box>
            );
          }
          return (
            <ChakraLink
              key={item.name}
              as={RouterLink}
              to={item.route}
              aria-current={isRouteActive(location.pathname, item.route) ? "page" : undefined}
              display="flex"
              alignItems="center"
              p="3"
              borderRadius="lg"
              className="app-nav-link"
              bg={isRouteActive(location.pathname, item.route) ? "var(--app-accent)" : "transparent"}
              _hover={{ bg: "var(--app-sidebar-hover)", textDecoration: "none" }}
              onClick={onNavigate}
            >
              {item.icon}
              <Text ml="3">{item.label}</Text>
            </ChakraLink>
          );
        })}
      </VStack>
    </Box>
  );
};

export const CustomLayout: React.FC<CustomLayoutProps> = ({ children, Title, menuItems }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { colorMode, toggleColorMode } = useColorMode();
  const { mutate: logout, isLoading: isLoggingOut } = useLogout();
  const toast = useToast();
  const location = useLocation();
  const currentItem = menuItems.flatMap((item) => [item, ...(item.children || [])]).find((item) => isRouteActive(location.pathname, item.route));
  const currentLabel = currentItem?.label || "운영 현황";
  const selectedEnvironment = getAdminEnvironment();

  React.useEffect(() => {
    void setupAdminWebPush().then((result) => {
      if (result.status === "denied") {
        toast({
          title: "Chrome 알림이 차단되어 있습니다",
          description: "브라우저 주소창의 알림 권한을 허용하면 새 감정 접수를 바로 받을 수 있습니다.",
          status: "warning",
          duration: 7000,
          isClosable: true,
          position: "top-right",
        });
      }
    });
  }, [toast]);

  return (
    <Box bg="var(--app-bg-page)" minH="100vh">
      {/* --- 데스크톱용 사이드바 --- */}
      {/* lg 사이즈 이상에서만 block으로 표시, 그 이하는 none으로 숨김 */}
      <SidebarContent
        menuItems={menuItems}
        display={{ base: "none", lg: "block" }}
      />

      {/* --- 모바일용 Drawer 사이드바 --- */}
      <Drawer isOpen={isOpen} placement="left" onClose={onClose}>
        <DrawerOverlay />
        <DrawerContent bg="var(--app-sidebar-bg)" maxW="min(86vw, 320px)">
          <SidebarContent menuItems={menuItems} onNavigate={onClose} w="full" borderRight="none" />
        </DrawerContent>
      </Drawer>

      {/* --- 메인 콘텐츠 영역 --- */}
      {/* 데스크톱에서는 사이드바 너비만큼 왼쪽 마진을 줌 */}
      <Box ml={{ base: 0, lg: "256px" }} transition=".3s ease">
        <Flex
          as="header"
          className="app-header"
          align="center"
          gap={{ base: 2, lg: 4 }}
          w="full"
          px="4"
          py="3"
          bg="var(--app-bg-card)"
          borderBottomWidth="1px"
          borderColor="var(--app-border)"
        >
          {/* --- 햄버거 메뉴 아이콘 --- */}
          {/* lg 사이즈 이상에서는 숨김 */}
          <IconButton
            aria-label="메뉴 열기"
            icon={<HamburgerIcon />}
            size="md"
            onClick={onOpen}
            display={{ base: "inline-flex", lg: "none" }}
            flexShrink={0}
            variant="ghost"
          />

          {/* 헤더 제목 (기존 Title 컴포넌트 활용) */}
          <Box flex="1" minW={0} textAlign="left">
             {/* 모바일에서는 로고가 중복되므로 텍스트로 대체하거나 숨길 수 있습니다. */}
             <Text className="app-header__eyebrow" display={{ base: "none", lg: "block" }}>
               QUAZAR ADMIN / OPERATIONS
             </Text>
             <Text className="app-header__title">{currentLabel}</Text>
          </Box>

          <HStack spacing={1} flexShrink={0}>
            <HStack spacing={1} borderWidth="1px" borderColor="var(--app-border)" borderRadius="md" p="1">
              {(Object.keys(ADMIN_ENVIRONMENTS) as AdminEnvironment[]).map((environment) => {
                const selected = environment === selectedEnvironment;
                const available = isAdminEnvironmentAvailable(environment);
                return (
                  <Button
                    key={environment}
                    size="sm"
                    variant={selected ? "solid" : "ghost"}
                    colorScheme={environment === "dev" ? "orange" : "blue"}
                    isDisabled={!available}
                    aria-pressed={selected}
                    title={!available ? `${ADMIN_ENVIRONMENTS[environment].label} 서버 연결 실패` : undefined}
                    onClick={() => {
                      if (selected || !available) return;
                      setAdminEnvironment(environment);
                      window.location.reload();
                    }}
                  >
                    {ADMIN_ENVIRONMENTS[environment].label}
                  </Button>
                );
              })}
            </HStack>
            <Tag size="sm" colorScheme={selectedEnvironment === "dev" ? "orange" : "blue"}>
              {selectedEnvironment === "dev" ? "개발 서버" : "운영 서버"}
            </Tag>
            <AdminNotificationBell />
            <Button
              size="sm"
              variant="ghost"
              onClick={() => logout()}
              isLoading={isLoggingOut}
              loadingText="로그아웃 중"
            >
              로그아웃
            </Button>
            <IconButton
              aria-label="다크 모드 전환"
              icon={colorMode === "light" ? <MoonIcon /> : <SunIcon />}
              size="md"
              variant="ghost"
              onClick={toggleColorMode}
            />
          </HStack>
        </Flex>

        <Box as="main" className="app-main" p={{ base: 3, md: 4, xl: 5 }}>
          {children}
        </Box>
      </Box>
    </Box>
  );
};
